import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mascaraTelefone } from '@/pages/inscricao/utils';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { validar, irParaOPrimeiro, mostrarToast, type CampoGuiado } from '@/lib/formularioGuiado';
import { chaveCarteiraLocal, carteiraParaUsar, formatarCentavos } from './utils';
import Folha from '@/components/movimento/Folha';
import {
  lerRascunho,
  guardarDadosContato,
  limparRascunho,
} from '@/lib/rascunhoFichas';
import type { PedidoAberto } from './types';

interface ItemCheckout {
  produto_id: number;
  nome: string;
  preco_centavos: number;
  quantidade: number;
}

interface ModalCheckoutProps {
  eventoId: number;
  eventoNome: string;
  eventoImagem: string | null;
  itens: ItemCheckout[];
  totalCentavos: number;
  quemPagaTaxa?: 'organizador' | 'comprador' | null;
  aoFechar: () => void;
}

export default function ModalCheckout({
  eventoId,
  eventoNome,
  eventoImagem,
  itens,
  totalCentavos,
  quemPagaTaxa,
  aoFechar,
}: ModalCheckoutProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  // O que já foi digitado volta ao reabrir a janela (mesmo aparelho).
  const [nome, setNome] = useState(() => lerRascunho(eventoId).nome);
  const [telefone, setTelefone] = useState(() => lerRascunho(eventoId).whatsapp);
  const [nomeCarteira, setNomeCarteira] = useState<string | null>(null);
  const [temCarteira, setTemCarteira] = useState(false);
  const [pularDados, setPularDados] = useState(false);
  const [verificando, setVerificando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<{ nome?: string; whatsapp?: string }>();

  // Detecta carteira salva neste celular e/ou login.
  useEffect(() => {
    let ativo = true;
    async function verificar() {
      const carteiraSalva = await carteiraParaUsar(eventoId);
      try {
        const logado = !!user;

        if (carteiraSalva) {
          if (ativo) {
            setTemCarteira(true);
            setPularDados(true);
          }
          // Busca o nome da carteira para a frase "Comprando na carteira de ...".
          try {
            const { data } = await supabase.rpc('loja_carteira_publica', {
              p_carteira: carteiraSalva,
            });
            const nomeCarteira = (data as { carteira?: { nome?: string } } | null)?.carteira?.nome;
            if (ativo && nomeCarteira) setNomeCarteira(nomeCarteira);
          } catch {
            /* segue sem o nome */
          }
        } else if (logado) {
          if (ativo) setPularDados(true);
        }
      } finally {
        if (ativo) setVerificando(false);
      }
    }
    verificar();
    return () => {
      ativo = false;
    };
  }, [eventoId, user]);

  // Guarda nome e WhatsApp enquanto a janela estiver fechada.
  useEffect(() => {
    if (pularDados) return;
    guardarDadosContato(eventoId, nome, telefone);
  }, [eventoId, nome, telefone, pularDados]);

  function esquecerCarteira() {
    localStorage.removeItem(chaveCarteiraLocal(eventoId));
    setTemCarteira(false);
    setNomeCarteira(null);
    setPularDados(false);
  }

  const telefoneValido = telefone.replace(/\D/g, '').length >= 10;

  function validarWhatsappNoBlur() {
    const d = telefone.replace(/\D/g, '');
    if (d.length > 0 && d.length < 10) {
      setErros((e) => ({ ...e, whatsapp: 'Informe seu WhatsApp para receber suas fichas.' }));
    }
  }

  // Nome só é cobrado depois que a pessoa sai do campo.
  function validarNomeNoBlur() {
    if (!nome.trim()) {
      setErros((e) => ({ ...e, nome: 'Falta seu nome.' }));
    }
  }

  async function pagar() {
    // Carrinho vazio não deveria acontecer, mas protege a pessoa.
    if (itens.length === 0) {
      setErro('Seu carrinho está vazio. Adicione um item para continuar.');
      mostrarToast('Seu carrinho está vazio.');
      return;
    }

    if (!pularDados) {
      const errosNovos = {
        nome: nome.trim() ? undefined : 'Falta seu nome.',
        whatsapp: telefoneValido
          ? undefined
          : 'Informe seu WhatsApp para receber suas fichas.',
      };
      setErros(errosNovos);
      const campos: CampoGuiado[] = [
        { ref: document.getElementById('checkout-nome'), rotulo: 'Seu nome', mensagem: errosNovos.nome ?? null },
        { ref: document.getElementById('checkout-whats'), rotulo: 'Seu WhatsApp', mensagem: errosNovos.whatsapp ?? null },
      ];
      const invalidos = validar(campos);
      if (invalidos.length > 0) {
        irParaOPrimeiro(invalidos);
        return;
      }
    }

    setGerando(true);
    setErro(null);
    const carteiraSalva = await carteiraParaUsar(eventoId);
    try {
      const { data, error } = await supabase.rpc('loja_abrir_pedido', {
        p_evento: eventoId,
        p_itens: itens.map((i) => ({ produto_id: i.produto_id, quantidade: i.quantidade })),
        p_nome: pularDados ? null : nome.trim() || null,
        p_telefone: pularDados ? null : telefone.replace(/\D/g, '') || null,
        p_carteira: carteiraSalva ?? null,
      });
      if (error) throw error;
      const aberto: PedidoAberto | undefined = Array.isArray(data) ? data[0] : (data as PedidoAberto);
      if (!aberto?.codigo) {
        setErro('Não conseguimos abrir seu pedido. Tente de novo.');
        return;
      }
      if (aberto.carteira_codigo) {
        localStorage.setItem(chaveCarteiraLocal(eventoId), aberto.carteira_codigo);
      }
      // Pedido criado: o rascunho das fichas já cumpriu o papel.
      limparRascunho(eventoId);
      navigate(`/loja/pedido/${aberto.codigo}`, {
        state: { eventoImagem, eventoNome },
      });
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos abrir seu pedido. Tente de novo.'));
    } finally {
      setGerando(false);
    }
  }

  return (
    <Folha
      aberto
      aoFechar={aoFechar}
      zIndex={50}
      padding={false}
      alturaMaxima="90vh"
      larguraMaxima="max-w-[520px]"
    >
        <div className="h-[8px] w-full bg-primary-500" />

        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-medium text-foreground-950">Finalizar pedido</h3>
              <p className="mt-0.5 text-sm text-foreground-600">{eventoNome}</p>
            </div>
            <button
              type="button"
              onClick={aoFechar}
              aria-label="Fechar"
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {verificando ? (
            <div className="py-8 text-center">
              <span className="mx-auto flex h-8 w-8 items-center justify-center text-primary-500">
                <i className="ri-loader-4-line animate-spin text-2xl" />
              </span>
            </div>
          ) : pularDados ? (
            <div className="mt-5 rounded-2xl border border-accent-200 bg-accent-50 px-4 py-3">
              <p className="flex items-center gap-2 text-sm font-medium text-accent-900">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-user-heart-line" />
                </span>
                {nomeCarteira ? `Comprando na carteira de ${nomeCarteira}` : 'Comprando na sua carteira'}
              </p>
              {temCarteira && (
                <button
                  type="button"
                  onClick={esquecerCarteira}
                  className="mt-1 cursor-pointer text-xs font-medium text-accent-700 underline"
                >
                  não é você?
                </button>
              )}
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              <div className="relative">
                <label
                  htmlFor="checkout-nome"
                  className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-foreground-600"
                >
                  Seu nome *
                </label>
                <div className={`flex h-[52px] items-center overflow-hidden rounded-xl border bg-white ${erros?.nome ? 'border-danger-500' : 'border-secondary-300'}`}>
                  <span className="flex h-full w-12 items-center justify-center text-foreground-400">
                    <i className="ri-user-line text-lg" />
                  </span>
                  <input
                    id="checkout-nome"
                    type="text"
                    value={nome}
                    onChange={(e) => {
                      setNome(e.target.value);
                      setErros((er) => (er ? { ...er, nome: undefined } : undefined));
                    }}
                    placeholder="Maria da Silva"
                    onBlur={validarNomeNoBlur}
                    className="h-full flex-1 bg-transparent pr-4 text-sm text-foreground-900 outline-none"
                  />
                </div>
                <p className="mt-1 text-xs text-foreground-500">É como a barraca vai te chamar.</p>
                {erros?.nome && (
                  <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                    {erros.nome}
                  </p>
                )}
              </div>

              <div className="relative">
                <label
                  htmlFor="checkout-whats"
                  className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-foreground-600"
                >
                  Seu WhatsApp *
                </label>
                <div className={`flex h-[52px] items-center overflow-hidden rounded-xl border bg-white ${erros?.whatsapp ? 'border-danger-500' : 'border-secondary-300'}`}>
                  <span className="flex h-full w-12 items-center justify-center text-green-600">
                    <i className="ri-whatsapp-line text-lg" />
                  </span>
                  <input
                    id="checkout-whats"
                    type="tel"
                    inputMode="numeric"
                    value={telefone}
                    onChange={(e) => {
                      setTelefone(mascaraTelefone(e.target.value));
                      setErros((er) => (er ? { ...er, whatsapp: undefined } : undefined));
                    }}
                    onBlur={validarWhatsappNoBlur}
                    placeholder="(00) 00000-0000"
                    className="h-full flex-1 bg-transparent pr-4 text-sm text-foreground-900 outline-none"
                  />
                </div>
                <p className="mt-1 text-xs text-foreground-500">
                  É por ele que você recupera suas fichas.
                </p>
                {erros?.whatsapp && (
                  <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                    {erros.whatsapp}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Resumo do pedido */}
          <div className="mt-5 rounded-2xl border border-secondary-200 bg-background-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
              Seu pedido
            </p>
            <div className="mt-3 space-y-2">
              {itens.map((i) => (
                <div key={i.produto_id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground-700">
                    {i.quantidade}× {i.nome}
                  </span>
                  <span className="text-foreground-800">
                    {formatarCentavos(i.preco_centavos * i.quantidade)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-secondary-200 pt-3">
              <span className="text-sm font-medium text-foreground-950">Total</span>
              <span className="text-base font-bold text-foreground-950">
                {formatarCentavos(totalCentavos)}
              </span>
            </div>
            {quemPagaTaxa === 'comprador' && (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-foreground-600">Taxa de serviço</span>
                <span className="text-xs text-foreground-500">somada no Pix</span>
              </div>
            )}
          </div>

          {erro && (
            <p className="mt-3 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
              {erro}
            </p>
          )}

          <p className="mt-4 text-[12px] leading-snug text-foreground-500">
            Depois do Pix suas fichas ficam aqui no celular. Mostre o QR na barraca.
          </p>

          <button
            type="button"
            onClick={pagar}
            disabled={gerando}
            className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:from-primary-600 hover:to-primary-700 disabled:cursor-not-allowed disabled:from-secondary-300 disabled:to-secondary-400 disabled:shadow-none"
          >
            {gerando ? (
              <span className="flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                Abrindo…
              </span>
            ) : (
              <>Pagar {formatarCentavos(totalCentavos)} no Pix</>
            )}
          </button>
        </div>
    </Folha>
  );
}